import { ReactNode, useEffect, useMemo, useState } from 'react';
import { Box, Skeleton } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { ProductVersionByIdQuery } from '../queries/__generated__/productVersionById.generated';
import FileUtils from '../common/utils/file.utils';
import SizedImage from '../common/components/SizedImage';

type Photo = NonNullable<ProductVersionByIdQuery['product']>[number]['base_units'][number]['photos'][number]['photo']

interface BaseUnitPhotosDisplay {
  mainPhoto?: Photo | null;
  photos: Photo[];
  productSubFamily: string;
  overlay?: ReactNode;
}

export default function BaseUnitPhotosDisplay({ mainPhoto, photos, productSubFamily, overlay }: BaseUnitPhotosDisplay): React.JSX.Element {
  const { t } = useTranslation(['products']);
  const [shouldDisplaySkeleton, setShouldDisplaySkeleton] = useState(true);
  const [photoOnMainDisplay, setPhotoOnMainDisplay] = useState<Photo | null | undefined>(mainPhoto);

  useEffect(() => {
    setPhotoOnMainDisplay(mainPhoto);
  }, [mainPhoto]);

  const handlePhotoSelection = (photo: Photo): void => {
    setPhotoOnMainDisplay(photo);
    setShouldDisplaySkeleton(true);
  };

  const sortedPhotos = useMemo(() => (
    photos.sort((photo) => (photo.path === mainPhoto?.path ? -1 : 0))
  ), [photos, mainPhoto]);

  const photosSelectionComponent = useMemo(() => {
    return sortedPhotos.map((photo) => {
      const isPhotoDisplayedOnMainDisplay = photo.path === photoOnMainDisplay?.path;

      return (
        <Box
          onClick={() => handlePhotoSelection(photo)}
          key={photo.path}
          component="img"
          alt={photo.path}
          src={FileUtils.BuildPackagingImageUrl({ photo, type: productSubFamily, size: { height: 48, width: 48, fit: 'contain' } })}
          sx={{
            borderRadius: 4,
            borderColor: isPhotoDisplayedOnMainDisplay ? 'outline.main' : 'transparent',
            borderWidth: 2,
            borderStyle: 'solid',
            '&:hover': {
              borderStyle: 'solid',
              borderColor: isPhotoDisplayedOnMainDisplay ? 'outline.main' : 'outlineVariant.main',
            },
            '&:active': {
              borderColor: 'outline.main',
            },
            p: 2,
            height: 48,
            width: 48,
            objectFit: 'contain',
            cursor: 'pointer',
          }}
        />
      );
    });
  }, [photoOnMainDisplay, sortedPhotos]);

  return (
    <Box
      sx={{
        position: 'relative',
        px: 2,
        gap: 1,
        display: 'flex',
        alignSelf: 'center',
        flexDirection: {
          lg: 'row',
          xs: 'column-reverse',
        },
      }}
    >
      {overlay && (
        <Box
          sx={{
            position: 'absolute',
            zIndex: 5,
            top: { xs: 260, lg: 8  },
            right: { xs: 6, md: 16, lg: 0 },
          }}
        >
          {overlay}
        </Box>
      )}
      {photosSelectionComponent.length > 1
        && (
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              mt: 2,
              maxHeight: { xs: 200, sm: 'inherit', lg: 600 },
              flexDirection: {
                lg: 'column',
                md: 'row',
              },
              justifyContent: { xs: 'center', lg: 'flex-start' },
              gap: 1,
            }}
          >
            {photosSelectionComponent}
          </Box>
        )}
      {
        shouldDisplaySkeleton
          && (
          <Skeleton
            variant="rectangular"
            sx={{
              mx: 'auto',
              width: {
                lg: 400,
                xs: 300,
              },
              height: {
                lg: 400,
                xs: 300,
              },

            }}
          />
          )
      }
      <SizedImage
        src={FileUtils.BuildPackagingImageUrl({
          photo: photoOnMainDisplay,
          type: productSubFamily,
          size: { height: 600, fit: 'cover' },
        })}
        alt={t('products:csu')}
        sx={{
          mx: 'auto',
          borderRadius: 0,
          height: shouldDisplaySkeleton ? 0 : 'inherit',
          width: shouldDisplaySkeleton ? 0 : undefined,
        }}
        width={{ xs: 300, lg: 496, xl: 600 }}
        height={{ xs: 300, lg: 496, xl: 600 }}
        onLoad={() => setShouldDisplaySkeleton(false)}
      />

    </Box>
  );
}
